import React, { useState, useEffect, useMemo } from 'react';
import { Input, Button, DatePicker, Toast, Tooltip, Table } from '@douyinfe/semi-ui';
import { API_USER } from '../helpers';
import { pageVisible } from '../helpers/utils';
import { openAgreementDialog } from './AgreementModal';
import { useT } from '../i18n';

// API Key 持久化:
//   - 用 sessionStorage(关闭浏览器即丢),不污染 localStorage 长期存储
//   - 写之前做轻度编码(base64),防止直接 grep `localStorage` 拿到明文
//   - 不是加密!XSS 仍然可读 sessionStorage;实际安全靠浏览器同源策略 + 无第三方脚本
const KEY_STORAGE = 'mt_user_apikey';
// 请求日志每页条数
const LOG_PAGE_SIZE = 10;
function encodeKey(k) { try { return btoa(unescape(encodeURIComponent(k))); } catch { return ''; } }
function decodeKey(s) { try { return decodeURIComponent(escape(atob(s))); } catch { return ''; } }

// ====== 全局状态:让 QuadGrid 和 Body 共享 usage/logs ======
let listeners = [];
let shared = {
  apikey: (typeof sessionStorage !== 'undefined' && decodeKey(sessionStorage.getItem(KEY_STORAGE))) || '',
  usage: null,
  logs: [],
  loading: false,
  queried: false,
  // 上一次查询是否失败。失败后要停掉静默轮询 ——
  // 否则会拿一个错的/失效的 Key 每 3 秒打一次接口,静默吞掉异常,用户完全看不到。
  queryFailed: false,
  gatewayOnline: null,
};

// 请求序号:只接受最后一次请求的响应。
// 手动查询和 3 秒静默刷新可能同时在飞,慢的旧响应后到会覆盖新结果 ——
// 表现是「输入了 B 键,面板却显示 A 键的用量」。
//
// 两个计数器,职责不同:
//   queryGen  —— 每次**手动查询**才 +1。静默刷新在开始时记下当时的 gen,
//                响应回来时 gen 变了(=用户已经查了新 Key)就丢弃这次结果。
//   manualSeq —— 手动查询之间的先后顺序,避免连续查两次时旧的那次后到覆盖新的。
let queryGen = 0;
let manualSeq = 0;

function useShared() {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((x) => x + 1);
    listeners.push(fn);
    return () => { listeners = listeners.filter((x) => x !== fn); };
  }, []);
  return shared;
}

// ====== 4 格卡(横铺整宽) ======
// 把毫秒差格式化成 "X 小时 Y 分钟后重置" / "X 天 Y 小时后重置"
function fmtCountdown(ms) {
  if (!ms || ms <= 0) return '';
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  if (days > 0) return `${days} 天 ${hours} 小时后重置`;
  if (hours > 0) return `${hours} 小时 ${mins} 分钟后重置`;
  return `${mins} 分钟后重置`;
}

const QuadGrid = () => {
  const t = useT();
  const s = useShared();

  useEffect(() => {
    API_USER.get('/api/status').then((r) => {
      const d = r.data?.data || {};
      setShared({
        gatewayOnline: d.online ?? null,
        // 把 /api/status 给的下次重置时间存到 shared,与 usage 取并集
        next_reset_5h_at: d.next_reset_5h_at || 0,
        next_reset_week_at: d.next_reset_week_at || 0,
        quota_reset_week_mode: d.quota_reset_week_mode || 'monday',
      });
    }).catch(() => setShared({ gatewayOnline: false }));
  }, []);

  // 每秒推进"剩余时间",让倒计时平滑刷新(每 30 秒强制同步一次 server_time 就不必要,先简单)
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 30000); // 30s 一刷
    return () => clearInterval(id);
  }, []);

  const usage5h = s.usage?.quota_5h ?? 0;
  const usageThisWeek = s.usage?.quota_week ?? 0;

  // 状态文案走翻译
  const gatewayStatusText =
    s.gatewayOnline === null ? t('首页.未知') :
    (s.gatewayOnline ? t('首页.在线') : t('首页.离线'));
  const gatewayDotColor = s.gatewayOnline === null ? '#c9cdd4' : (s.gatewayOnline ? '#22c55e' : '#f56565');

  // 套餐内总调用 = 已用 + 剩余(若无限则显示无限)
  const planTotal = s.usage
    ? (s.usage.unlimited
        ? '∞'
        : ((s.usage.used_quota ?? s.usage.calls_used ?? 0) + ' / ' + (s.usage.plan_total_calls ?? s.usage.total_calls ?? 0)))
    : '—';

  // 倒计时 — tick 是为了让定时器更新触发 re-render
  const nowMs = Date.now() + tick * 0; // 用 tick 占位,触发重渲染
  const reset5hAt = s.usage?.next_reset_5h_at || s.next_reset_5h_at || 0;
  const resetWkAt = s.usage?.next_reset_week_at || s.next_reset_week_at || 0;
  // 倒计时:next_reset_5h_at > now 才展示,异常值(>5h+1min)视为无意义跳过
  const MAX_COUNTDOWN_MS_5H = 5 * 3600 * 1000 + 60000; // 最多 5h+1min,超过视为无意义
  const cd5h = (reset5hAt > nowMs && reset5hAt - nowMs <= MAX_COUNTDOWN_MS_5H)
    ? fmtCountdown(reset5hAt - nowMs) : '';
  const cdWk = (resetWkAt > nowMs && resetWkAt - nowMs <= 8 * 24 * 3600 * 1000)
    ? fmtCountdown(resetWkAt - nowMs) : '';

  return (
    <div className="quad-grid">
      <div className="mini-card">
        <div className="mini-card__label">
          <span>{t('首页.累计消耗')}</span>
          <Tooltip content={t('首页.累计消耗说明')} position="top">
            <button className="mini-card__hint" aria-label="info">!</button>
          </Tooltip>
        </div>
        <div className="mini-card__value">
          {s.usage
            ? (s.usage.unlimited
                ? t('首页.无限')
                : ((s.usage.used_quota ?? s.usage.calls_used ?? 0).toLocaleString() + ' / ' + (s.usage.plan_total_calls ?? s.usage.total_calls ?? 0).toLocaleString() + ' ' + t('首页.次')))
            : '—'}
        </div>
      </div>

      <div className="mini-card">
        <div className="mini-card__label">
          <span>{t('首页.5小时消耗')}</span>
          <Tooltip content={t('首页.5小时说明')} position="top">
            <button className="mini-card__hint" aria-label="info">!</button>
          </Tooltip>
        </div>
        <div className="mini-card__value">
          {s.usage ? usage5h.toLocaleString() + ' / ' + (s.usage.limit_5h || '∞') + ' ' + t('首页.次') : '—'}
        </div>
        {cd5h && <div style={{ fontSize: 10, color: '#a1a1aa', textAlign: 'center', marginTop: 2 }}>{cd5h}</div>}
      </div>

      <div className="mini-card">
        <div className="mini-card__label">
          <span>{t('首页.本周消耗')}</span>
          <Tooltip content={t('首页.本周说明')} position="top">
            <button className="mini-card__hint" aria-label="info">!</button>
          </Tooltip>
        </div>
        <div className="mini-card__value">
          {s.usage ? usageThisWeek.toLocaleString() + ' / ' + (s.usage.limit_week || '∞') + ' ' + t('首页.次') : '—'}
        </div>
        {cdWk && <div style={{ fontSize: 10, color: '#a1a1aa', textAlign: 'center', marginTop: 2 }}>{cdWk}</div>}
      </div>

      <div className="mini-card">
        <div className="mini-card__label mini-card__label--between">
          <span>{t('首页.网关状态')}</span>
          <span className="mini-card__dot" style={{ background: gatewayDotColor }} />
        </div>
        <div className="mini-card__value" style={{ color: s.gatewayOnline === false ? '#f56565' : (s.gatewayOnline ? '#00b42a' : '#86909c') }}>
          {gatewayStatusText}
        </div>
      </div>
    </div>
  );
};

// ====== 主体(API Key / 日志 / 图表) ======
const Body = () => {
  const t = useT();
  const s = useShared();
  const [apikey, setApikey] = useState(shared.apikey);

  // 监听窗口大小,确保窄屏下的表格列宽响应式生效
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const today = new Date();
  const last7 = new Date();
  last7.setDate(today.getDate() - 7);
  const [dateRange, setDateRange] = useState([last7, today]);
  // 日志表分页页码(受控)。后台每 3s 静默刷新会换掉 dataSource,
  // Semi Table 在数据变化时会把内部页码重置为 1,导致正在翻页的用户被弹回第一页。
  // 这里自己持有 currentPage,刷新时保持不动。
  const [logPage, setLogPage] = useState(1);

  // 初始化:如果 localStorage 里有 key,自动拉一次数据(只跑一次)
  useEffect(() => {
    const cached = (typeof sessionStorage !== 'undefined' && decodeKey(sessionStorage.getItem(KEY_STORAGE))) || '';
    if (cached && /^sk-[a-zA-Z0-9]{20,}$/.test(cached)) {
      fetchData(cached);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 协议闸门(服务使用知情确认书):
  //   用户第一次用这张卡时,必须先阅读并**手动勾选**同意,勾选记录入库。
  //   没勾 → 弹窗取消 → 本次查询不继续(但 Key 仍可用,只是没签约)。
  //   协议接口本身异常时**放行** —— 不能因为一个附加校验把查询功能搞挂。
  const ensureAgreement = async (key) => {
    let d = null;
    try {
      const r = await API_USER.get('/api/agreement/current', {
        headers: { Authorization: `Bearer ${key}` },
      });
      d = r.data?.data || null;
    } catch (e) {
      console.warn('[UserPanel] 协议状态查询失败,本次放行:', e?.message);
      return true;
    }
    if (!d || !d.required || d.signed) return true;
    const ok = await openAgreementDialog({ doc: d, apiKey: key });
    if (!ok) Toast.warning('需先勾选同意《服务使用知情确认书》后才能使用本服务');
    return !!ok;
  };

  const fetchData = async (key) => {
    if (!key) { Toast.warning(t('公共.请先输入Key')); return; }
    if (!/^sk-[a-zA-Z0-9]{20,}$/.test(key)) { Toast.error(t('公共.格式错误')); return; }
    // 第一次用这张卡 → 先过协议闸门
    const agreed = await ensureAgreement(key);
    if (!agreed) return;
    try { sessionStorage.setItem(KEY_STORAGE, encodeKey(key)); } catch {}
    setApikey(key);
    // 关键:把 apikey 一起 patch 进 shared,否则外部订阅者(TicketsPanel)拿到的 apikey 还是旧的
    setShared({ apikey: key, loading: true, queried: true, queryFailed: false, usage: null, logs: [] });

    const gen = ++queryGen;
    const seq = ++manualSeq;
    let failed = false;
    try {
      const r = await API_USER.get('/api/usage/token/', {
        headers: { Authorization: `Bearer ${key}` },
      });
      if (gen !== queryGen || seq !== manualSeq) return; // 已有更新的查询在飞,丢弃过期响应
      if (r.data && r.data.code) {
        setShared({ usage: r.data.data });
      } else {
        failed = true;
        Toast.error(r.data?.message || t('公共.查询失败'));
      }
    } catch (e) {
      if (gen !== queryGen || seq !== manualSeq) return;
      failed = true;
      const msg = e.response?.data?.message;
      if (msg) Toast.error(msg);
      else Toast.error(t('公共.查询失败'));
    }

    try {
      const r = await API_USER.get('/api/log/token', {
        headers: { Authorization: `Bearer ${key}` },
      });
      if (gen !== queryGen || seq !== manualSeq) return;
      if (r.data?.success) {
        setShared({ logs: [...r.data.data] });
      }
    } catch {}

    // 查询失败:停掉静默轮询,并把失败状态留在界面上。
    // 以前失败后 queried 还是 true、apikey 也存着 → 3 秒静默刷新会拿这个错的 Key
    // 一直重试,而且异常被空 catch 吞掉,用户只看到"数据一直是空的"。
    if (failed) setShared({ queryFailed: true, loading: false });
    else setShared({ loading: false });
  };

  // 静默拉取(供 setInterval 使用):不弹 Toast,不重置 loading
  const silentRefresh = async (key) => {
    if (!key) return;
    // 记下发起时的"代":如果这期间用户手动查了别的 Key(gen 变了),这次结果就作废
    const gen = queryGen;
    try {
      const r1 = await API_USER.get('/api/usage/token/', { headers: { Authorization: `Bearer ${key}` } });
      if (gen !== queryGen) return;
      if (r1.data && r1.data.code) setShared({ usage: r1.data.data, queried: true });
    } catch {}
    try {
      const r2 = await API_USER.get('/api/log/token', { headers: { Authorization: `Bearer ${key}` } });
      if (gen !== queryGen) return;
      if (r2.data?.success) setShared({ logs: [...r2.data.data] });
    } catch {}
  };

  // 定期静默刷新 — 3 秒一次,依赖 shared.apikey
  // 两个闸门:
  //   1) 上一次查询失败 → 不再自动重试(拿错 Key 打接口没意义,还静默吞异常)
  //   2) 页面不可见(切到别的标签页/最小化)→ 没必要继续打
  useEffect(() => {
    if (!shared.apikey) return undefined;
    if (shared.queryFailed) return undefined;
    const id = setInterval(() => {
      if (!pageVisible()) return;
      silentRefresh(shared.apikey);
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shared.apikey, shared.queryFailed]);

  const filteredLogs = useMemo(() => {
    if (!dateRange || dateRange.length !== 2) return s.logs;
    const [a, b] = dateRange;
    const sTs = a ? new Date(a).setHours(0, 0, 0, 0) : 0;
    const eTs = b ? new Date(b).setHours(23, 59, 59, 999) : Infinity;
    return s.logs.filter((l) => l.created_at >= sTs && l.created_at <= eTs);
  }, [s.logs, dateRange]);

  const chartData = useMemo(() => {
    const map = new Map();
    const fmtDay = (ms) => {
      const d = new Date(ms);
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    };
    filteredLogs.forEach((l) => {
      const d = fmtDay(l.created_at);
      map.set(d, (map.get(d) || 0) + (l.quota || 0));
    });
    if (map.size === 0) return [];
    const sortedKeys = Array.from(map.keys()).sort();
    const start = new Date(sortedKeys[0] + 'T00:00:00');
    const end = new Date(sortedKeys[sortedKeys.length - 1] + 'T00:00:00');
    const result = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      result.push({ date: key, quota: map.get(key) || 0 });
    }
    return result;
  }, [filteredLogs]);

  // 表格列文案(走 i18n)
  // 列宽策略:桌面端给固定宽度更整齐;窄屏(<768px)改自适应并允许横向滚动
  // 避免在某些手机浏览器上小宽度列被压缩到看不见
  const isNarrow = windowWidth < 768;
  // 兜底:数据变少导致当前页越界时(如筛掉大量日志),回退到最后一页,
  // 否则 Semi 会渲染出空白表格
  const maxLogPage = Math.max(1, Math.ceil(filteredLogs.length / LOG_PAGE_SIZE));
  useEffect(() => {
    if (logPage > maxLogPage) setLogPage(maxLogPage);
  }, [logPage, maxLogPage]);

  // 实际传给 Table 的数据:只取当前页这一段
  const pagedLogs = useMemo(() => {
    const start = (logPage - 1) * LOG_PAGE_SIZE;
    return filteredLogs.slice(start, start + LOG_PAGE_SIZE);
  }, [filteredLogs, logPage]);

  const logColumns = [
    {
      // 用 token 内调用序号(seq,后端 ROW_NUMBER 按 token_id 分组计数)而非全局 logs.id
      // 这样不同 token 各自从 #1 开始,与管理员后台保持一致
      title: t('日志.ID'), dataIndex: 'seq', width: isNarrow ? 56 : 64,
      render: (s, row) => <code style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, color: '#4e5969' }} title={'logs.id=' + row.id}>#{s}</code>,
    },
    {
      title: t('日志.时间'), dataIndex: 'created_at', width: isNarrow ? 150 : 160,
      render: (ts) => new Date(ts).toLocaleString('zh-CN', { hour12: false }),
    },
    { title: t('日志.模型'), dataIndex: 'model', width: isNarrow ? 140 : 160, render: (m) => <code style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{m}</code> },
    {
      title: t('日志.次数'), dataIndex: 'quota', width: 70,
      // 失败请求 quota=0,直接写 "-0" 很难看 —— 显示 "0"(确实没扣费)
      render: (q) => <span style={{ color: '#f56565', fontWeight: 500 }}>{q ? '-' + q : '0'}</span>,
    },
    {
      title: t('日志.状态'), dataIndex: 'status', width: 70,
      // status=2 = 被网关自身拦下(5 小时/本周限额、余额用尽、模型未授权),
      // 或流式响应后超额撤回 —— 这类请求要么根本没发出去,要么已经拿到内容,
      // 都不是「调用失败」,所以用中性灰而不是红色,鼠标悬停可看具体原因。
      // status=3 = 客户端自己断开(掐流/关连接):同样不是网关的失败,
      // 用橙色单独标出,别混进「失败」里 —— 混进去既冤枉网关,也让用户
      // 以为自己请求有问题。悬停可看到是否已按上游生成时长计费。
      render: (st, r) => st === 1
        ? <span style={{ color: '#00b42a' }}>{t('日志.成功')}</span>
        : (st === 2
          ? (
            <Tooltip content={String(r.content || '')}>
              <span style={{ color: '#86909c' }}>{t('日志.已拦截')}</span>
            </Tooltip>
          )
          : (st === 3
            ? (
              <Tooltip content={String(r.content || '')}>
                <span style={{ color: '#ff7d00' }}>{t('日志.已断开')}</span>
              </Tooltip>
            )
            : <span style={{ color: '#f56565' }}>{t('日志.失败')}</span>)),
    },
  ];

  return (
    <>
      {/* API Key */}
      <div className="card">
        <h2 className="card__title">{t('首页.我的APIKey')}</h2>
        <div className="key-input">
          <Input
            className="key-input__field"
            value={apikey}
            onChange={setApikey}
            placeholder="sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            mode="password"
            onKeyDown={(e) => e.key === 'Enter' && fetchData(apikey)}
          />
          <Button
            type="primary"
            theme="solid"
            loading={s.loading}
            onClick={() => fetchData(apikey)}
          >
            {t('首页.查询')}
          </Button>
        </div>
        {s.queried && s.usage && <KeyExpiry usage={s.usage} />}
      </div>

      {/* 请求日志 */}
      {s.queried && (
        <div className="card">
          <h2 className="card__title">{t('日志.请求日志')}</h2>
          <div className="log-toolbar">
            <DatePicker
              type="dateRange"
              className="log-toolbar__date"
              value={dateRange}
              onChange={(v) => { setDateRange(v); setLogPage(1); }}
            />
            <Button
              className="log-toolbar__btn"
              onClick={() => { setDateRange([last7, today]); setLogPage(1); }}
            >
              {t('日志.恢复7天')}
            </Button>
          </div>
          {filteredLogs.length === 0 ? (
            <div style={{ marginTop: 12, fontSize: 13, color: '#86909c', textAlign: 'center', padding: '20px 0' }}>
              {t('日志.暂无日志')}
            </div>
          ) : (
            <div className="log-table-wrap" style={{ marginTop: 12 }}>
              <Table
                // key 只在「有数据 → 无数据」时变一次,用于重置表格内部状态。
                // 不能用 filteredLogs.length:后台每 3s 静默刷新,只要新增日志
                // 条数就会变,key 变会导致 Table 整体重建、分页被弹回第 1 页。
                key={filteredLogs.length > 0 ? 'has-logs' : 'empty'}
                size="small"
                rowKey="id"
                pagination={{
                  currentPage: logPage,
                  pageSize: LOG_PAGE_SIZE,
                  total: filteredLogs.length,
                  hideOnSinglePage: true,
                  onPageChange: setLogPage,
                }}
                columns={logColumns}
                // 自己切片再传:只靠 Semi 的 pagination.currentPage,
                // 在 dataSource 频繁变化时它会把所有行都渲染出来(实测 50 条全在一页),
                // 分页器却仍显示"第1条-第10条"。切片逻辑放在外面更可控。
                dataSource={pagedLogs}
                scroll={{ x: 'max-content' }}
              />
            </div>
          )}
        </div>
      )}

      {/* 过去 7 天用量 */}
      {s.queried && (
        <div className="card">
          <h2 className="card__title">{t('日志.近7天')}</h2>
          {chartData.length === 0 ? (
            <div className="chart-placeholder">{t('日志.无数据')}</div>
          ) : (
            <MiniChart data={chartData} />
          )}
        </div>
      )}
    </>
  );
};

// 暴露给外部的订阅器:让 TicketsPanel 能跟随 shared 变化重新渲染
const _extListeners = new Set();
function notifyExt() { _extListeners.forEach((fn) => fn()); }
function setShared(patch) {
  shared = { ...shared, ...patch };
  listeners.forEach((fn) => fn());
  notifyExt();
}
export function useUserShared() {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((x) => x + 1);
    _extListeners.add(fn);
    return () => { _extListeners.delete(fn); };
  }, []);
  return shared;
}

// ====== API Key 过期时间信息卡 ======
const STATUS_TEXT = {
  1: { textKey: '令牌.状态.使用中', color: '#00b42a' },
  2: { textKey: '令牌.状态.已禁用', color: '#86909c' },
  3: { textKey: '令牌.状态.已过期', color: '#f56565' },
  4: { textKey: '令牌.状态.已耗尽', color: '#f56565' },
  5: { textKey: '令牌.状态.待激活', color: '#ff7d00' },
};

function formatTime(ms) {
  if (!ms || ms <= 0) return '';
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
    + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
}

function KeyExpiry({ usage }) {
  const t = useT();
  const expiresAt = usage?.expires_at || 0;
  const createdAt = usage?.created_at || 0;
  const activatedAt = usage?.activated_at || 0;
  const status = usage?.status ?? 1;
  const st = STATUS_TEXT[status] || STATUS_TEXT[1];

  const isPending = status === 5 && (!activatedAt || activatedAt === 0);

  // 剩余时间(走 i18n)
  let expiryLabel = t('key.永不过期');
  let expiryColor = '#00b42a';
  let remainingDays = null;

  if (isPending) {
    expiryLabel = t('key.待激活');
    expiryColor = '#ff7d00';
  } else if (expiresAt > 0) {
    const remainingMs = expiresAt - Date.now();
    if (remainingMs <= 0) {
      expiryLabel = t('令牌.状态.已过期');
      expiryColor = '#f56565';
    } else {
      const d = Math.floor(remainingMs / 86400000);
      const h = Math.floor((remainingMs % 86400000) / 3600000);
      const m = Math.floor((remainingMs % 3600000) / 60000);
      remainingDays = d;
      if (d >= 1) {
        expiryLabel = `${d} ${t('key.天')} ${h} ${t('key.小时')}`;
      } else if (h >= 1) {
        expiryLabel = `${h} ${t('key.小时')} ${m} ${t('key.分钟')}`;
      } else {
        expiryLabel = `${m} ${t('key.分钟')}`;
      }
      expiryColor = d <= 3 ? '#f56565' : (d <= 7 ? '#ff7d00' : '#1d7dfa');
    }
  }

  const items = [
    { label: t('key.创建时间'), value: createdAt > 0 ? formatTime(createdAt) : '—' },
  ];
  if (isPending) {
    items.push({ label: t('key.过期时间'), value: t('key.待激活说明'), color: expiryColor });
  } else {
    items.push({ label: t('key.过期时间'), value: expiresAt > 0 ? formatTime(expiresAt) : t('key.永不过期'), color: expiryColor });
  }
  if (!isPending && expiresAt > 0) {
    items.push({ label: t('key.剩余'), value: expiryLabel, color: expiryColor });
  }
  items.push({ label: t('公共.操作') === 'Actions' ? 'Status' : '状态', value: t(st.textKey), color: st.color });

  return (
    <div className="key-expiry">
      {items.map((it, i) => (
        <span key={i} className="key-expiry__item">
          <span className="key-expiry__label">{it.label}</span>
          <span className="key-expiry__value" style={{ color: it.color || '#1f2329' }}>
            {it.value}
          </span>
        </span>
      ))}
    </div>
  );
}

const MiniChart = ({ data }) => {
  const t = useT();
  const w = 520;
  const h = 200;
  const padL = 40;
  const padR = 16;
  const padT = 8;
  const padB = 24;
  const [hover, setHover] = useState(null);
  const xs = data.map((d) => d.date);
  const ys = data.map((d) => d.quota);
  const maxY = Math.max(1, ...ys);
  const total = ys.reduce((a, b) => a + b, 0);
  const peak = ys.length ? Math.max(...ys) : 0;
  const peakDate = peak ? xs[ys.indexOf(peak)] : null;

  // 单日数据
  if (data.length === 1) {
    return (
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 12, color: '#86909c' }}>
          <span>{t('图表.区间合计')} <b style={{ color: '#1f2329' }}>{total}</b> {t('首页.次')}</span>
          <span>{t('图表.峰值')} <b style={{ color: '#1f2329' }}>{peak}</b> · {peakDate ? peakDate.slice(5) : '—'}</span>
        </div>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(180deg, #f0f7ff 0%, #f7faff 100%)',
          border: '1px solid #e0eaff', borderRadius: 10, padding: '24px 16px',
        }}>
          <div style={{ fontSize: 13, color: '#4e5969', marginBottom: 6 }}>{data[0].date}</div>
          <div style={{ fontSize: 36, fontWeight: 700, color: '#1d7dfa', lineHeight: 1.1 }}>{data[0].quota}</div>
          <div style={{ fontSize: 12, color: '#86909c', marginTop: 6 }}>{t('图表.仅一天')}</div>
        </div>
      </div>
    );
  }

  // 多日折线图
  const xStep = data.length > 1 ? (w - padL - padR) / (data.length - 1) : 0;
  const points = data.map((d, i) => {
    const x = padL + i * xStep;
    const y = padT + (h - padT - padB) * (1 - d.quota / maxY);
    return { x, y, d, i };
  });
  const polylineStr = points.map((p) => p.x + ',' + p.y).join(' ');
  const areaStr = points.length > 0
    ? points.map((p) => p.x + ',' + p.y).join(' ')
      + ' ' + (padL + (points.length - 1) * xStep) + ',' + (h - padB)
      + ' ' + padL + ',' + (h - padB)
    : '';

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 12, color: '#86909c' }}>
        <span>{t('图表.区间合计')} <b style={{ color: '#1f2329' }}>{total}</b> {t('首页.次')}</span>
        <span>{t('图表.峰值')} <b style={{ color: '#1f2329' }}>{peak}</b> · {peakDate ? peakDate.slice(5) : '—'}</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 200, display: 'block', overflow: 'visible' }}>
        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
          const y = padT + (h - padT - padB) * (1 - p);
          const label = p === 1 ? maxY : p === 0 ? 0 : Math.round(maxY * p);
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={w - padR} y2={y} stroke="#f0f1f3" strokeWidth="1" />
              <text x={padL - 6} y={y + 3} fontSize="10" fill="#a3a8b3" textAnchor="end">{label}</text>
            </g>
          );
        })}
        {areaStr && <polygon points={areaStr} fill="#1d7dfa" fillOpacity="0.12" />}
        <polyline points={polylineStr} fill="none" stroke="#1d7dfa" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }}>
            <circle cx={p.x} cy={p.y} r={hover === i ? 6 : 4} fill="#1d7dfa" stroke="#fff" strokeWidth="2" />
            <circle cx={p.x} cy={p.y} r="14" fill="transparent" />
          </g>
        ))}
        {xs.map((x, i) => {
          const step = Math.max(1, Math.ceil(xs.length / 7));
          if (i % step !== 0 && i !== xs.length - 1) return null;
          const cx = padL + i * xStep;
          return (
            <text key={i} x={cx} y={h - 6} fontSize="10" fill="#86909c" textAnchor="middle">
              {x.slice(5)}
            </text>
          );
        })}
      </svg>
      {hover != null && points[hover] && (
        <div style={{
          position: 'absolute',
          top: (points[hover].y / h * 200) - 8 + 'px',
          left: (points[hover].x / w * 100) + '%',
          transform: 'translate(-50%, -100%)',
          background: 'rgba(31,35,41,0.92)', color: '#fff', padding: '6px 10px', borderRadius: 6, fontSize: 12,
          whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 10,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          <div style={{ fontWeight: 600 }}>{xs[hover]}</div>
          <div>{t('首页.次')}: {ys[hover]}</div>
        </div>
      )}
    </div>
  );
};

const UserPanel = { QuadGrid, Body };
export default UserPanel;